import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Minus, Plus, ShoppingBag, Trash2, Check, Truck, Store, MapPin, MessageSquare, AlarmClock, Star, Heart } from "lucide-react";

// JS Date.getDay(): 0=Sun, 1=Mon, ... 6=Sat
const DELIVERY_OPTIONS = [
  { key: "nhs",     group: "Northern HS Affiliates", time: "9:00 AM",  minutes: 540, days: [1, 2, 3, 4, 5] },
  { key: "ebb",     group: "EBB Affiliates",         time: "11:00 AM", minutes: 660, days: [1, 2, 4, 5] },
  { key: "church",  group: "Church Folk",            time: "10:45 AM", minutes: 645, days: [0] },
];

// Pickup: Mon–Sat only, 7:00 AM through 3:00 PM every 30 min.
const PICKUP_TIMES = (() => {
  const list = [];
  const to12h = (h, m) => {
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
  };
  for (let m = 7 * 60; m <= 15 * 60; m += 30) {
    list.push({ minutes: m, time: to12h(Math.floor(m / 60), m % 60) });
  }
  return list;
})();
const PICKUP_DAYS = new Set([1, 2, 3, 4, 5, 6]); // Mon–Sat
const LEAD_MINUTES = 60;

// EST/EDT-aware "now" helpers. Cutoffs are anchored to America/New_York.
function estParts() {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date()).reduce((a, x) => (a[x.type] = x.value, a), {});
  return {
    iso: `${p.year}-${p.month}-${p.day}`,
    minutes: parseInt(p.hour, 10) * 60 + parseInt(p.minute, 10),
  };
}

// Pickup cutoff (minutes past midnight EST) for same-day orders.
// Wed: every pickup by 7:00 AM. Mon/Tue/Thu/Fri: slots ≤ 10:30 AM by 7 AM,
// slots ≥ 11:00 AM by 10 AM. Saturday & Sunday: no cutoff (use 1-hour lead).
function pickupCutoffMinutes(dow, slotMinutes) {
  if (dow === 3) return 7 * 60;
  if ([1, 2, 4, 5].includes(dow)) {
    return slotMinutes <= 10 * 60 + 30 ? 7 * 60 : 10 * 60;
  }
  return null;
}
function fmtMinutes(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(min).padStart(2, "0")} ${suffix}`;
}

function currency(n) { return `$${Number(n || 0).toFixed(2)}`; }

// Upcoming same-day pickup cutoff (if we're inside its reminder window).
// Returns { cutoff, mins_left, label } or null.
const REMINDER_WINDOW = 30; // show reminder within last 30 min before a cutoff
function upcomingCutoff() {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    weekday: "short",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date()).reduce((a, x) => (a[x.type] = x.value, a), {});
  const nowMin = parseInt(p.hour, 10) * 60 + parseInt(p.minute, 10);
  const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = dowMap[p.weekday] ?? -1;
  const cutoffs = [];
  if (dow === 3) {
    cutoffs.push({ at: 7 * 60, label: "Wednesday pickup orders (all windows)" });
  } else if ([1, 2, 4, 5].includes(dow)) {
    cutoffs.push({ at: 7 * 60, label: "Morning pickups (7:30–10:30 AM)" });
    cutoffs.push({ at: 10 * 60, label: "Late-morning pickups (11 AM – 3 PM)" });
  }
  const upcoming = cutoffs
    .map((c) => ({ ...c, mins_left: c.at - nowMin }))
    .filter((c) => c.mins_left > 0 && c.mins_left <= REMINDER_WINDOW)
    .sort((a, b) => a.mins_left - b.mins_left)[0];
  return upcoming || null;
}

function CutoffBanner() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(iv);
  }, []);
  const up = upcomingCutoff();
  if (!up) return null;
  return (
    <div
      data-testid="cutoff-reminder-banner"
      className="mb-6 rounded-lg border border-[#D69F4C]/50 bg-[#D69F4C]/12 p-4 flex items-start gap-3"
    >
      <AlarmClock className="h-5 w-5 text-[#D69F4C] mt-0.5 shrink-0" strokeWidth={1.75} />
      <div className="flex-1">
        <p className="label-eyebrow text-[#1A1514]">Same-day cutoff soon</p>
        <p className="mt-1 text-sm text-[#1A1514]">
          <span className="font-semibold">{up.label}</span> close in{" "}
          <span data-testid="cutoff-minutes-left" className="font-mono-jet text-[#C05A46]">
            {up.mins_left} min
          </span>{" "}
          at{" "}
          <span className="font-mono-jet">{fmtMinutes(up.at)} EST</span>.
        </p>
      </div>
    </div>
  );
}

function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function dayName(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
function isSameLocalDay(iso) { return iso === todayIso(); } // eslint-disable-line no-unused-vars
function currentMinutes() { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); } // eslint-disable-line no-unused-vars

function slotsForIso(iso) {
  if (!iso) return [];
  const dow = new Date(iso + "T00:00:00").getDay();
  const out = [];
  DELIVERY_OPTIONS.forEach((o) => {
    if (o.days.includes(dow)) {
      out.push({
        key: `d-${o.key}`,
        type: "delivery",
        group: o.group,
        time: o.time,
        minutes: o.minutes,
        label: `${o.group} — ${o.time}`,
      });
    }
  });
  if (PICKUP_DAYS.has(dow)) {
    PICKUP_TIMES.forEach((t) => {
      out.push({
        key: `p-${t.minutes}`,
        type: "pickup",
        group: null,
        time: t.time,
        minutes: t.minutes,
        label: `Pickup — ${t.time}`,
      });
    });
  }
  return out;
}

function OptionCard({ selected, onClick, name, price, description, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`selectable-card w-full text-left p-4 ${selected ? "is-selected" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-serif-display text-lg leading-tight">{name}</p>
          {description ? <p className="mt-1 text-xs text-[#7B7573]">{description}</p> : null}
        </div>
        {price > 0 ? <span className="font-mono-jet text-xs text-[#4A4341]">+{currency(price)}</span> : null}
      </div>
      {selected ? <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#C05A46]"><Check className="h-3.5 w-3.5" /> Selected</span> : null}
    </button>
  );
}

export default function Order() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const signedIn = !!user && user.role === "customer";
  const [menu, setMenu] = useState({ drinks: [], sizes: [], milks: [], syrups: [], extras: [] });
  const [loading, setLoading] = useState(true);
  const [closedForDay, setClosedForDay] = useState(false);

  const [drink, setDrink] = useState(null);
  const [size, setSize] = useState(null);
  const [milk, setMilk] = useState(null);
  const [syrups, setSyrups] = useState([]);
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [cart, setCart] = useState([]);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [slotKey, setSlotKey] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [pickupPopupOpen, setPickupPopupOpen] = useState(false);
  const [pendingOrderNumber, setPendingOrderNumber] = useState("");

  // Favorites for signed-in customers.
  const [favorites, setFavorites] = useState([]);
  const [saveAsFavorite, setSaveAsFavorite] = useState(false);
  const [favoriteName, setFavoriteName] = useState("");

  const loadFavorites = async () => {
    try {
      const { data } = await api.get("/customers/me/favorites");
      setFavorites(data);
    } catch (e) {
      console.debug("Failed to load favorites", e);
    }
  };

  useEffect(() => {
    if (signedIn) loadFavorites();
  }, [signedIn]);

  const applyFavorite = (fav) => {
    setCart(fav.items);
    toast.success(`Loaded "${fav.name}" · review and checkout`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Prefill name/phone from signed-in customer profile.
  useEffect(() => {
    if (signedIn) {
      if (user.name && !customerName) setCustomerName(user.name);
      if (user.phone && !customerPhone) setCustomerPhone(user.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, user?.name, user?.phone]);

  // Reorder: if navigated here from /account with a prefill cart, load it.
  useEffect(() => {
    const pre = location.state?.prefillCart;
    if (Array.isArray(pre) && pre.length > 0) {
      setCart(pre);
      toast.success(
        location.state?.fromOrder
          ? `Reordering from ${location.state.fromOrder}`
          : "Cart pre-filled"
      );
      // Clear state so a refresh doesn't re-apply.
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slotList = slotsForIso(pickupDate);
  const est = estParts();
  const isToday = pickupDate === est.iso;
  const dow = pickupDate ? new Date(pickupDate + "T00:00:00").getDay() : null;
  const availableSlots = slotList.map((s) => {
    if (!isToday) return { ...s, disabled: false, hint: "" };
    if (s.type === "pickup") {
      const cutoff = pickupCutoffMinutes(dow, s.minutes);
      if (cutoff !== null) {
        return {
          ...s,
          disabled: est.minutes >= cutoff,
          hint: `Cutoff ${fmtMinutes(cutoff)} EST`,
        };
      }
    }
    // Delivery + Sat/Sun pickup: 1-hour lead time.
    return {
      ...s,
      disabled: s.minutes < est.minutes + LEAD_MINUTES,
      hint: "≥ 1 hour ahead",
    };
  });
  const selectedSlot = availableSlots.find((s) => s.key === slotKey) || null;

  useEffect(() => { setSlotKey(""); }, [pickupDate]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: s }] = await Promise.all([api.get("/menu"), api.get("/settings")]);
        setMenu(m);
        setClosedForDay(!!s.closed_for_day);
        const first = m.drinks.find((d) => d.active);
        if (first) setDrink(first);
        const firstSize = (m.sizes || []).find((x) => x.active);
        if (firstSize) setSize(firstSize.id);
      } catch { toast.error("Couldn't load the menu"); }
      finally { setLoading(false); }
    })();
  }, []);

  const activeSizes = (menu.sizes || []).filter((s) => s.active);
  const activeSize = activeSizes.find((s) => s.id === size) || activeSizes[0] || null;
  const sizeModifier = activeSize ? Number(activeSize.price || 0) : 0;
  const lineTotal = useMemo(() => {
    if (!drink) return 0;
    const syrupSum = syrups.reduce((s, id) => s + (menu.syrups.find((x) => x.id === id)?.price || 0), 0);
    const extraSum = extras.reduce((s, id) => s + (menu.extras.find((x) => x.id === id)?.price || 0), 0);
    const milkAdd = milk ? (menu.milks.find((m) => m.id === milk)?.price || 0) : 0;
    const per = drink.price + sizeModifier + milkAdd + syrupSum + extraSum;
    return Math.max(0, per) * quantity;
  }, [drink, size, milk, syrups, extras, quantity, menu, sizeModifier]);

  const cartTotal = cart.reduce((sum, c) => sum + c.line_total, 0);
  const toggleId = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const resetBuilder = () => {
    setSize(activeSizes[0]?.id || null);
    setMilk(null); setSyrups([]); setExtras([]); setNotes(""); setQuantity(1);
  };

  const addToCart = () => {
    if (!drink) return toast.error("Pick a drink first");
    if (!activeSize) return toast.error("Pick a size first");
    const line = {
      drink: { id: drink.id, name: drink.name, price: drink.price },
      size: activeSize.name,
      milk: milk ? (() => { const m = menu.milks.find((x) => x.id === milk); return m ? { id: m.id, name: m.name, price: m.price } : null; })() : null,
      syrups: syrups.map((id) => { const s = menu.syrups.find((x) => x.id === id); return { id: s.id, name: s.name, price: s.price }; }),
      extras: extras.map((id) => { const e = menu.extras.find((x) => x.id === id); return { id: e.id, name: e.name, price: e.price }; }),
      quantity, notes, line_total: Number(lineTotal.toFixed(2)),
    };
    setCart((c) => [...c, line]);
    toast.success(`${drink.name} added to your order`);
    resetBuilder();
  };
  const removeCartLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const submitOrder = async () => {
    if (closedForDay) return toast.error("The Cozy Bean is closed today.");
    if (!customerName.trim() || !customerPhone.trim() || !pickupDate || !slotKey)
      return toast.error("Name, phone, date, and a window are required");
    if (!selectedSlot) return toast.error("Please pick a window");
    if (selectedSlot.disabled) {
      const reason = selectedSlot.hint || "1 hour lead time";
      return toast.error(`That window is past its cutoff (${reason}).`);
    }
    if (cart.length === 0) return toast.error("Your order is empty");

    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        fulfillment_type: selectedSlot.type,
        pickup_date: pickupDate,
        pickup_time: selectedSlot.label,
        delivery_group: selectedSlot.type === "delivery" ? selectedSlot.group : "",
        delivery_address: "",
        notes: orderNotes.trim(),
        items: cart,
      });
      toast.success(`Order placed · ${data.order_number}`);

      // Persist favorite if requested.
      if (signedIn && saveAsFavorite && favoriteName.trim()) {
        try {
          await api.post("/customers/me/favorites", {
            name: favoriteName.trim(),
            items: cart,
          });
          toast.success(`Saved "${favoriteName.trim()}" to your favorites`);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save favorite");
        }
      }
      setSaveAsFavorite(false);
      setFavoriteName("");

      setCheckoutOpen(false);
      if (selectedSlot.type === "pickup") {
        setPendingOrderNumber(data.order_number);
        setPickupPopupOpen(true);
      } else {
        navigate(`/order/success/${data.order_number}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't place order");
    } finally { setSubmitting(false); }
  };

  const activeDrinks = menu.drinks.filter((d) => d.active);
  const activeMilks = menu.milks.filter((m) => m.active);
  const activeSyrups = menu.syrups.filter((s) => s.active);
  const activeExtras = menu.extras.filter((e) => e.active);

  return (
    <div data-testid="order-page" className="min-h-screen grain-overlay">
      <SiteHeader cartCount={cart.length} />

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="mb-10">
          <p className="label-eyebrow">Build your drink</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">Craft it your way.</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5">
              <Truck className="h-3.5 w-3.5 text-[#C05A46]" /> Delivery to affiliate groups only
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5">
              <Store className="h-3.5 w-3.5 text-[#7A8B76]" /> Pickup Mon–Sat, 7 AM – 3 PM
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5">
              <MapPin className="h-3.5 w-3.5 text-[#D69F4C]" /> Dillsburg, PA
            </span>
          </div>
          <div className="mt-4 rounded-lg border border-black/10 bg-white/70 p-4 text-sm text-[#4A4341] max-w-3xl">
            <p className="label-eyebrow text-[#1A1514]">Delivery groups</p>
            <ul className="mt-2 space-y-1">
              <li><span className="font-medium">Northern HS Affiliates</span> — Mon–Fri, 9:00 AM</li>
              <li><span className="font-medium">EBB Affiliates</span> — Mon, Tue, Thu, Fri, 11:00 AM</li>
              <li><span className="font-medium">Church Folk</span> — Sundays, 10:45 AM</li>
            </ul>
          </div>
        </div>

        {closedForDay ? (
          <div data-testid="closed-banner" className="mb-8 rounded-lg border border-[#C05A46]/40 bg-[#C05A46]/10 p-6">
            <p className="label-eyebrow text-[#C05A46]">Closed today</p>
            <p className="mt-2 font-serif-display text-xl">The Cozy Bean isn&rsquo;t taking orders right now.</p>
            <p className="mt-1 text-sm text-[#4A4341]">Please check back tomorrow — thanks for your patience.</p>
          </div>
        ) : (
          <CutoffBanner />
        )}

        {!signedIn ? (
          <div data-testid="signin-prompt-banner" className="mb-6 rounded-lg border border-black/10 bg-white p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-[#4A4341]">
              <span className="font-semibold text-[#1A1514]">Ordering as a guest.</span>{" "}
              Sign in to save this order, reorder favorites in one tap, and follow your latest order without a tracking number.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/signin" data-testid="signin-prompt-signin-link"
                className="rounded-full border border-[#C05A46]/40 px-4 py-1.5 text-xs font-semibold text-[#C05A46] hover:bg-[#C05A46] hover:text-white transition-colors">
                Sign in
              </a>
              <a href="/register" data-testid="signin-prompt-register-link"
                className="rounded-full bg-[#C05A46] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#A84C3A] transition-colors">
                Create account
              </a>
            </div>
          </div>
        ) : (
          <div data-testid="signed-in-banner" className="mb-6 rounded-lg border border-[#7A8B76]/40 bg-[#7A8B76]/10 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-sm text-[#1A1514]">
              <span className="font-semibold">Welcome back, {user.name?.split(" ")[0]}.</span>{" "}
              This order will be saved to your account for easy reorder.
            </p>
            <a href="/account" data-testid="signed-in-account-link"
              className="rounded-full border border-black/15 px-4 py-1.5 text-xs font-semibold hover:border-[#1A1514] transition-colors">
              Your account →
            </a>
          </div>
        )}

        {signedIn && favorites.length > 0 && !closedForDay ? (
          <section data-testid="favorites-strip" className="mb-8 rounded-2xl border border-[#D69F4C]/40 bg-[#D69F4C]/6 p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-[#D69F4C]" fill="#D69F4C" strokeWidth={0} />
                <p className="label-eyebrow text-[#1A1514]">Your favorites — 1-tap reorder</p>
              </div>
              <a href="/account" data-testid="favorites-manage-link" className="text-[11px] text-[#7B7573] hover:text-[#1A1514]">
                Manage →
              </a>
            </div>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
              {favorites.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`favorite-quick-${f.id}`}
                  onClick={() => applyFavorite(f)}
                  className="shrink-0 min-w-[220px] max-w-[280px] text-left rounded-xl border border-black/10 bg-white p-3 hover:border-[#C05A46] transition-colors"
                >
                  <p className="font-serif-display text-base leading-tight">{f.name}</p>
                  <p className="mt-1 text-[11px] text-[#7B7573] line-clamp-2">
                    {f.items.map((l) => `${l.quantity}× ${l.drink.name}`).join(" · ")}
                  </p>
                  <p className="mt-2 font-mono-jet text-xs text-[#C05A46]">{currency(f.total)} · Tap to load</p>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {loading ? (
          <p data-testid="order-loading" className="text-[#7B7573]">Loading the menu…</p>
        ) : activeDrinks.length === 0 ? (
          <div data-testid="empty-menu-notice" className="selectable-card p-10 text-center">
            <p className="font-serif-display text-2xl">The menu is being prepared.</p>
            <p className="mt-2 text-sm text-[#7B7573]">The owner hasn&rsquo;t added any drinks yet. Please check back shortly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-10">
              <section>
                <h2 className="font-serif-display text-2xl tracking-tight mb-4">01 · Choose a drink</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeDrinks.map((d) => (
                    <OptionCard key={d.id} testid={`drink-option-${d.id}`} selected={drink?.id === d.id}
                      onClick={() => setDrink(d)} name={d.name} description={d.description} price={d.price} />
                  ))}
                </div>
              </section>

              {activeSizes.length > 0 && (
                <section>
                  <h2 className="font-serif-display text-2xl tracking-tight mb-4">02 · Size</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {activeSizes.map((s) => (
                      <OptionCard key={s.id} testid={`size-option-${s.id}`} selected={size === s.id}
                        onClick={() => setSize(s.id)} name={s.name}
                        price={Number(s.price || 0) > 0 ? Number(s.price) : 0}
                        description={s.description || ""} />
                    ))}
                  </div>
                </section>
              )}

              {activeMilks.length > 0 && (
                <section>
                  <h2 className="font-serif-display text-2xl tracking-tight mb-4">03 · Milk</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <OptionCard testid="milk-option-none" selected={milk === null} onClick={() => setMilk(null)} name="No milk" price={0} />
                    {activeMilks.map((m) => (
                      <OptionCard key={m.id} testid={`milk-option-${m.id}`} selected={milk === m.id}
                        onClick={() => setMilk(m.id)} name={m.name} price={m.price} />
                    ))}
                  </div>
                </section>
              )}

              {activeSyrups.length > 0 && (
                <section>
                  <h2 className="font-serif-display text-2xl tracking-tight mb-4">04 · Syrups</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {activeSyrups.map((s) => (
                      <OptionCard key={s.id} testid={`syrup-option-${s.id}`} selected={syrups.includes(s.id)}
                        onClick={() => toggleId(syrups, setSyrups, s.id)} name={s.name} price={s.price} />
                    ))}
                  </div>
                </section>
              )}

              {activeExtras.length > 0 && (
                <section>
                  <h2 className="font-serif-display text-2xl tracking-tight mb-4">05 · Extras</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {activeExtras.map((e) => (
                      <OptionCard key={e.id} testid={`extra-option-${e.id}`} selected={extras.includes(e.id)}
                        onClick={() => toggleId(extras, setExtras, e.id)} name={e.name} price={e.price} />
                    ))}
                  </div>
                </section>
              )}

              <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="item-notes" className="label-eyebrow text-[#4A4341]">Barista notes (optional)</Label>
                  <Textarea id="item-notes" data-testid="item-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Extra hot, light foam…" rows={3}
                    className="mt-2 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
                </div>
                <div>
                  <p className="label-eyebrow text-[#4A4341]">Quantity</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" data-testid="quantity-decrement-btn"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))} className="rounded-full border-black/15"><Minus className="h-4 w-4" /></Button>
                    <span data-testid="quantity-value" className="font-serif-display text-3xl w-12 text-center">{quantity}</span>
                    <Button type="button" variant="outline" size="icon" data-testid="quantity-increment-btn"
                      onClick={() => setQuantity(quantity + 1)} className="rounded-full border-black/15"><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              </section>

              <div className="flex items-center justify-between border-t border-black/10 pt-6">
                <div>
                  <p className="label-eyebrow text-[#4A4341]">Item subtotal</p>
                  <p data-testid="line-total" className="font-serif-display text-3xl mt-1">{currency(lineTotal)}</p>
                </div>
                <Button type="button" data-testid="add-to-cart-btn" onClick={addToCart}
                  className="btn-brand rounded-full px-8 py-6 text-sm font-semibold">Add to order</Button>
              </div>
            </div>

            <aside className="lg:sticky lg:top-24 h-fit">
              <div className="selectable-card p-6" data-testid="cart-panel">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5 text-[#C05A46]" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-xl">Your order</h3>
                  </div>
                  <span data-testid="cart-count" className="font-mono-jet text-xs text-[#7B7573]">
                    {cart.length} item{cart.length === 1 ? "" : "s"}
                  </span>
                </div>

                {cart.length === 0 ? (
                  <p className="text-sm text-[#7B7573]">Add drinks from the builder to start your order.</p>
                ) : (
                  <ul className="space-y-4 max-h-80 overflow-y-auto pr-1">
                    {cart.map((line, i) => (
                      <li key={i} data-testid={`cart-line-${i}`} className="pb-4 border-b border-black/5 last:border-none">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium">{line.quantity}× {line.drink.name}
                              <span className="text-[#7B7573] text-xs ml-2">{line.size}</span></p>
                            <p className="mt-1 text-xs text-[#4A4341]">
                              {[line.milk?.name, ...line.syrups.map((s) => s.name), ...line.extras.map((e) => e.name)]
                                .filter(Boolean).join(" · ") || "As-is"}
                            </p>
                            {line.notes ? <p className="mt-1 text-xs italic text-[#7B7573]">&ldquo;{line.notes}&rdquo;</p> : null}
                          </div>
                          <div className="text-right">
                            <p className="font-mono-jet text-sm">{currency(line.line_total)}</p>
                            <button type="button" onClick={() => removeCartLine(i)} data-testid={`cart-remove-${i}`}
                              className="mt-1 text-[#7B7573] hover:text-[#C05A46] transition-colors"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-6 pt-4 border-t border-black/10 flex items-center justify-between">
                  <span className="label-eyebrow text-[#4A4341]">Total</span>
                  <span data-testid="cart-total" className="font-serif-display text-2xl">{currency(cartTotal)}</span>
                </div>

                <Button type="button" data-testid="checkout-btn" disabled={cart.length === 0 || closedForDay}
                  onClick={() => setCheckoutOpen(true)} className="btn-brand w-full mt-5 rounded-full py-6 text-sm font-semibold">
                  Continue to checkout
                </Button>
                <p className="mt-3 text-[11px] text-[#7B7573] text-center">Pay cash or e-transfer on arrival · Same-day cutoffs apply.</p>
              </div>
            </aside>
          </div>
        )}
      </main>

      {checkoutOpen ? (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          data-testid="checkout-modal" onClick={() => !submitting && setCheckoutOpen(false)}>
          <div className="bg-[#F5F5F0] w-full max-w-lg rounded-2xl border border-black/10 p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <p className="label-eyebrow">Almost there</p>
            <h3 className="mt-3 font-serif-display text-3xl tracking-tight">Your details</h3>
            <p className="mt-2 text-sm text-[#4A4341]">
              I&rsquo;ll review each order and confirm it before I start brewing. Same-day cutoffs apply — see below.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="customer-name">Name</Label>
                <Input id="customer-name" data-testid="checkout-name-input" value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
              </div>
              <div>
                <Label htmlFor="customer-phone">Phone number</Label>
                <Input id="customer-phone" data-testid="checkout-phone-input" value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)} placeholder="717.555.5555"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46] font-mono-jet" />
                <p className="mt-1 text-[11px] text-[#7B7573]">Used to reach you about your order and — for pickup — to text the pickup address after confirmation.</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label htmlFor="pickup-date">Date</Label>
                  <Input id="pickup-date" type="date" min={todayIso()} data-testid="checkout-date-input"
                    value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                    className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
                </div>
                <div>
                  <Label>Window</Label>
                  <Select value={slotKey} onValueChange={setSlotKey} disabled={!pickupDate}>
                    <SelectTrigger data-testid="checkout-slot-select"
                      className="mt-1 bg-white border-black/10 focus:ring-1 focus:ring-[#C05A46]">
                      <SelectValue placeholder={pickupDate ? (availableSlots.length ? "Choose a window" : "No windows on that day") : "Pick a date first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {availableSlots.map((s) => (
                        <SelectItem key={s.key} value={s.key} disabled={s.disabled} data-testid={`slot-option-${s.key}`}>
                          {s.label}{s.disabled ? ` — past cutoff (${s.hint})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-[#7B7573]">
                    Delivery: affiliate groups only · Pickup Mon–Sat, 7 AM – 3 PM.
                  </p>
                  <div className="mt-2 rounded border border-black/10 bg-white p-2 text-[11px] text-[#4A4341] leading-relaxed">
                    <p className="font-semibold text-[#1A1514]">Same-day pickup cutoffs (EST)</p>
                    <ul className="mt-0.5 space-y-0.5">
                      <li>Mon, Tue, Thu, Fri · pickups <span className="whitespace-nowrap">7:30–10:30 AM</span> → order by <span className="whitespace-nowrap">7:00 AM</span></li>
                      <li>Mon, Tue, Thu, Fri · pickups <span className="whitespace-nowrap">11:00 AM–3:00 PM</span> → order by <span className="whitespace-nowrap">10:00 AM</span></li>
                      <li>Wednesday · all pickups → order by <span className="whitespace-nowrap">7:00 AM</span></li>
                      <li>Saturday & deliveries · at least 1 hour ahead</li>
                    </ul>
                  </div>
                </div>
              </div>

              {selectedSlot ? (
                <div data-testid="fulfillment-indicator"
                  className={`rounded-lg border p-4 ${selectedSlot.type === "pickup" ? "border-[#7A8B76]/40 bg-[#7A8B76]/10" : "border-[#C05A46]/40 bg-[#C05A46]/10"}`}>
                  <div className="flex items-center gap-2">
                    {selectedSlot.type === "pickup"
                      ? <Store className="h-4 w-4 text-[#7A8B76]" />
                      : <Truck className="h-4 w-4 text-[#C05A46]" />}
                    <p className="label-eyebrow text-[#1A1514]">
                      {selectedSlot.type === "pickup" ? "Pickup" : `Delivery — ${selectedSlot.group}`}
                    </p>
                  </div>
                  <p className="mt-1 text-sm text-[#1A1514]">
                    {dayName(pickupDate)} · {selectedSlot.time}
                  </p>
                  {selectedSlot.type === "pickup" ? (
                    <p className="mt-2 text-xs text-[#4A4341] inline-flex items-start gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-[#D69F4C]" />
                      I&rsquo;ll text the pickup address to your phone once your order is confirmed.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div>
                <Label htmlFor="order-notes">Order notes (optional)</Label>
                <Textarea id="order-notes" data-testid="checkout-notes-input" rows={2}
                  value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]" />
              </div>

              {signedIn ? (
                <div data-testid="save-favorite-checkout-block" className="rounded-lg border border-[#D69F4C]/40 bg-[#D69F4C]/6 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="checkout-save-favorite-toggle"
                      checked={saveAsFavorite}
                      onChange={(e) => setSaveAsFavorite(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[#C05A46]"
                    />
                    <span className="flex-1">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1514]">
                        <Heart className="h-3.5 w-3.5 text-[#C05A46]" /> Save this order as a favorite
                      </span>
                      <span className="mt-1 block text-[11px] text-[#7B7573]">
                        Reorder it in one tap from your account or the top of this page next time.
                      </span>
                    </span>
                  </label>
                  {saveAsFavorite ? (
                    <div className="mt-3">
                      <Label htmlFor="checkout-fav-name" className="text-xs">Favorite name</Label>
                      <Input
                        id="checkout-fav-name"
                        data-testid="checkout-favorite-name-input"
                        value={favoriteName}
                        onChange={(e) => setFavoriteName(e.target.value)}
                        maxLength={60}
                        placeholder="Morning latte, Sunday church order…"
                        className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-6 flex items-center justify-between pt-4 border-t border-black/10">
              <span className="label-eyebrow text-[#4A4341]">Total due on arrival</span>
              <span className="font-serif-display text-2xl">{currency(cartTotal)}</span>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={() => setCheckoutOpen(false)} disabled={submitting} data-testid="checkout-cancel-btn">Back</Button>
              <Button type="button" data-testid="checkout-submit-btn" onClick={submitOrder} disabled={submitting}
                className="btn-brand rounded-full px-6 py-6 text-sm font-semibold">
                {submitting ? "Placing…" : "Place order"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <AlertDialog open={pickupPopupOpen} onOpenChange={setPickupPopupOpen}>
        <AlertDialogContent data-testid="pickup-address-popup" className="bg-[#F5F5F0] border-black/10">
          <AlertDialogHeader>
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#D69F4C] text-white mb-2">
              <MessageSquare className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <AlertDialogTitle className="font-serif-display text-2xl text-center tracking-tight">
              Order received!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-[#4A4341] leading-relaxed">
              You&rsquo;ll receive a text at the phone number you provided with your pickup address
              once your order is confirmed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="pickup-popup-ok-btn"
              onClick={() => {
                setPickupPopupOpen(false);
                navigate(`/order/success/${pendingOrderNumber}`);
              }}
              className="btn-brand rounded-full px-6"
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
